import { useEffect } from 'react';
import { stripUnreadPrefix, titleWithUnread } from './notificationList';

/**
 * The ONE writer of `document.title`: pages contribute their title, the bell
 * its unread count, and the tab shows `(n) <page title>`.
 *
 * Both used to write the title directly and overwrote each other (E2E 30.09.):
 * opening Verläufe dropped the "(6) " prefix until the count changed, and a
 * page restoring its "previous" title brought back a stale count or kept its
 * own title on other pages.
 */

interface PageTitle {
  token: symbol;
  title: string;
}

let appTitle = '';
let unread = 0;
const pages: PageTitle[] = [];

const contributing = () => unread > 0 || pages.length > 0;

function render(): void {
  const base = pages.length ? pages[pages.length - 1].title : appTitle;
  const next = titleWithUnread(base, unread);
  if (document.title !== next) document.title = next;
}

/** The app's own title is whatever the tab shows while nothing contributes. */
function captureAppTitle(): void {
  if (!contributing()) appTitle = stripUnreadPrefix(document.title);
}

/** The bell's unread count ("(n) " prefix); 0 removes the prefix. */
export function setUnreadInTitle(count: number): void {
  captureAppTitle();
  unread = Math.max(0, Math.floor(count) || 0);
  render();
}

/** A page's title while it is open; the returned function releases it (any order). */
export function pushPageTitle(title: string): () => void {
  captureAppTitle();
  const entry: PageTitle = { token: Symbol(title), title };
  pages.push(entry);
  render();
  return () => {
    const index = pages.findIndex((page) => page.token === entry.token);
    if (index === -1) return;
    pages.splice(index, 1);
    render();
  };
}

/** `pushPageTitle` for the lifetime of a component. */
export function usePageTitle(title: string): void {
  useEffect(() => pushPageTitle(title), [title]);
}
